import { useContext, useEffect, useMemo, useState } from "react";
import { FiltersAuthserver } from "src/components/filters/FilterAuthserver";
import { DefaultPageHeader } from "src/components/headers/defaultPageHeader";
import { SimpleTable } from "src/components/tables/simpleTable";
import AddEditAuthserver from "src/containers/addEditAuthserver";
import { AppContext } from "src/contexts";
import { useAuthserversRowsColumns } from "src/data/authserverData";
import { useAuthServer } from "src/hooks/useAuthservers";
import { useEnvironments } from "src/hooks/useEnvironments";
import DeleteModal from "src/layouts/modal/deleteModal";
import { EAuthserverTypeOptions } from "src/utils";

function AuthserverList() {
  const columns = useAuthserversRowsColumns();
  const [openModal, setOpenModal] = useState(false);
  const { organizationId } = useContext(AppContext);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [authServerSelected, setAuthServerSelected] = useState<any>();
  const [authServerToDelete, setAuthServerToDelete] = useState<any>();
  const [filteredEnvironmentId, setFilteredEnvironmentId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const {
    authservers,
    isLoading,
    isLoadingForm,
    isLoadingDeletion,
    fetchAuthservers,
    createAuthserver,
    updateAuthserver,
    deleteAuthserver
  } = useAuthServer({
    organizationId,
  });
  const { environments, getEnvironments } = useEnvironments({ organizationId });

  useEffect(() => {
    fetchAuthservers();
    getEnvironments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authServerSelected) {
      setOpenModal(true);
      setIsEdit(true);
    }
  }, [authServerSelected]);

  useEffect(() => {
    if (authServerToDelete) {
      setOpenDeleteModal(true);
    }
  }, [authServerToDelete]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleOptionEnvironmentChange = (value: string) => {
    setFilteredEnvironmentId(value);
  };

  const handleOpenEditAuthServer = (data: any) => {
    setAuthServerSelected(data);
  };

  const handleOpenDeleteAuthServer = (data: any) => {
    setAuthServerToDelete(data);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setAuthServerSelected(undefined);
    setIsEdit(false);
  };

  const handleCloseDeleteModal = () => {
    setAuthServerToDelete(undefined);
    setOpenDeleteModal(false);
  }

  const handleCreateAuthServer = async (data: any) => {
    try {
      const payload = {
        metadata: {
          name: data.name,
        },
        spec: {
          type: data.type,
          ...(data.type === EAuthserverTypeOptions.oidc && {
            clientId: data.clientId,
            clientSecret: data.clientSecret,
            issuerURL: data.issuerURL,
            redirectPath: data.redirectPath,
            usernameClaim: data.usernameClaim,
            emailClaim: data.emailClaim,
            roleClaim: data.roleClaim,
            groupsClaim: data.groupsClaim,
            authorizedRedirectDomains: data.domains,
          }),
          ...(data.type === EAuthserverTypeOptions.basic && {
            credentials: {
              basic: data.basicCredentials,
            }
          }),
          ...(data.type === EAuthserverTypeOptions.token && {
            credentials: {
              token: data.tokens,
            }
          }),
        },
        environmentID: data.environmentID,
      };
      const success = await createAuthserver(payload);
      if (success) {
        setOpenModal(false);
      }
    } catch (error) {
      console.log({ error });
    }
  };

  const handleUpdateAuthServer = async (data: any) => {
    try {
      const payload = {
        id: authServerSelected.id,
        metadata: {
          name: data.name,
        },
        spec: {
          type: data.type,
          ...(data.type === EAuthserverTypeOptions.oidc && {
            clientId: data.clientId,
            clientSecret: data.clientSecret,
            issuerURL: data.issuerURL,
            redirectPath: data.redirectPath,
          }),
          ...(data.type === EAuthserverTypeOptions.basic && {
            credentials: {
              basic: data.basicCredentials,
            }
          }),
          ...(data.type === EAuthserverTypeOptions.token && {
            credentials: {
              token: data.tokens,
            }
          }),
          usernameClaim: data.usernameClaim,
          emailClaim: data.emailClaim,
          roleClaim: data.roleClaim,
          groupsClaim: data.groupsClaim,
          authorizedRedirectDomains: data.domains,
          basicRealm: authServerSelected.spec.basicRealm,
        },
        environmentID: data.environmentID,
      };
      const success = await updateAuthserver(payload);
      if (success) {
        setOpenModal(false);
        setIsEdit(false);
      }
    } catch (error) {
      console.log({ error });
    }
  };

  const handleDeleteAuthserver = async () => {
    try {
      const success = await deleteAuthserver(authServerToDelete.environmentID, authServerToDelete.id);
      if (success) {
        setOpenDeleteModal(false);
        setAuthServerToDelete(undefined);
      }
    } catch (error) {
      
    }
  }

  const dataToShow = useMemo(() => {
    const dataToReturn =  authservers.filter((item) => {
      const matchesEnvironment =
        filteredEnvironmentId === "" || item.environmentID === filteredEnvironmentId;
  
      const lowerCaseSearch = searchTerm.trim().toLowerCase();
      const matchesSearch =
        searchTerm === "" ||
        item.metadata?.name?.toLowerCase()?.includes(lowerCaseSearch)
  
      return matchesEnvironment && matchesSearch;
    });
    return dataToReturn.sort((a, b) => {
      const dateA = new Date(a.lastUpdatedDate || 0);
      const dateB = new Date(b.lastUpdatedDate || 0);
      return dateB.getTime() - dateA.getTime(); 
    });
  }, [filteredEnvironmentId, searchTerm, authservers]);



  return (
    <div className="flex flex-col gap-4 m-4">
      <DefaultPageHeader
        title="Authserver"
        onAdd={() => setOpenModal(true)}
        buttonLabel="Add authserver"
      />
      <hr className="border-t border-gray-200" />
      <FiltersAuthserver
        onSearch={handleSearch}
        onOptionEnvironmentChange={handleOptionEnvironmentChange}
        optionsEnvironment={environments}
      />
      <hr className="border-t border-gray-200" />
      <div className="flex flex-row items-center justify-between py-3">
        <div className="text-sm font-semibold text-gray-700">
          List of Authserver
        </div>
      </div>
      <SimpleTable
        columns={columns}
        rows={dataToShow as any[]}
        withElevation={false}
        onRowDelete={handleOpenDeleteAuthServer}
        onRowEdit={handleOpenEditAuthServer}
        withCheckBox
        withAction
        isLoading={isLoading}
        searchTerm={searchTerm || (filteredEnvironmentId.length ? "(filter: Environment)" : "") }
      />
      <AddEditAuthserver
        open={openModal}
        onClose={handleCloseModal}
        onSubmit={isEdit ? handleUpdateAuthServer : handleCreateAuthServer}
        title={isEdit ? "Edit Auth Server" : "Add Auth Server"}
        description="Input your auth server details below."
        submitTitleButton={isEdit ? "Update Auth server" : "Add Authserver"}
        isLoading={isLoadingForm}
        defaultValues={authServerSelected}
        isEdition={isEdit}
        environments={environments}
      />

      <DeleteModal
        open={openDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleDelete={handleDeleteAuthserver}
        title="Delete Authserve"
        isLoading={isLoadingDeletion}
        confirmationValue={authServerToDelete?.metadata?.name}
      />
    </div>
  );
}

export default AuthserverList;
